import { Icon } from '@components/Elements/Icon'
import { Text } from '@components/Elements/Text'
import { t } from '@lib/i18n'
import { useAuthStore } from '@stores/auth'
import { DeviceTypeEnum } from '@vetahealth/tuna-can-api'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import React from 'react'
import { ScrollView } from 'react-native'
import styled, { useTheme } from 'styled-components/native'

const Wrapper = styled.View`
  margin-bottom: 30px;
  gap: 14px;
`
const Container = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 14px;
`
const IconWrapper = styled.View`
  width: 60px;
  height: 60px;
  background-color: ${({ theme }) => theme.colors.tertiaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.xLarge}px;
  align-items: center;
  justify-content: center;
`
const InfoWrapper = styled.View`
  gap: 2px;
`
const DeviceWrapper = styled.View`
  flex-direction: row;
  gap: 5px;
  margin-top: 3px;
`
const Device = styled.View`
  padding: 8px 15px;
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  background-color: ${({ theme }) => theme.colors.deviceBackground};
`

function getDeviceLabel(deviceType: DeviceTypeEnum): string {
  const deviceLabel: Record<DeviceTypeEnum, string> = {
    Generic_BPCuff: t('settings.devices.genericBpCuff'),
    Generic_Button: t('settings.devices.genericButton'),
    Generic_PulseOximeter: t('settings.devices.genericPulseOximeter'),
    Generic_Scale: t('settings.devices.genericScale'),
    Generic_Glucometer: t('settings.devices.genericGlucometer'),
    Bodytrace_BPCuff: t('settings.devices.bodytraceBpCuff'),
    Bodytrace_Scale: t('settings.devices.bodytraceScale'),
    SmartMeter_iBloodPressure: t('settings.devices.smartMeterIBloodPressure'),
    SmartMeter_iBloodPressureXL: t('settings.devices.smartMeterIBloodPressureXL'),
    SmartMeter_iGlucose: t('settings.devices.smartMeterIGlucose'),
    SmartMeter_iPulseOx: t('settings.devices.smartMeterIPulseOx'),
    SmartMeter_iScale: t('settings.devices.smartMeterIScale'),
    Garmin_Watch: t('settings.devices.garminWatch'),
  }

  return deviceLabel[deviceType]
}

export function Profile(): React.ReactNode | null {
  const theme = useTheme()
  const [user] = useAuthStore((state) => [state.user])
  const name = `${user?.firstName} ${user?.lastName}`

  if (!user) return null

  return (
    <Wrapper>
      <Container>
        <IconWrapper>
          <Icon icon={['fad', 'user']} size={26} color={theme.colors.primary} />
        </IconWrapper>
        <InfoWrapper>
          <Text weight="semiBold" size="heading4" disableLineHeight>
            {name}
          </Text>
          <Text weight="semiBold" size="settings" disableLineHeight>
            {!!user.phone && parsePhoneNumberWithError(user.phone).format('INTERNATIONAL')}
          </Text>
        </InfoWrapper>
      </Container>
      <ScrollView horizontal>
        <DeviceWrapper>
          {user?.deviceTypes.map((deviceType) => (
            <Device key={deviceType}>
              <Text size="tiny" weight="semiBold" disableLineHeight color={theme.colors.deviceText}>
                {getDeviceLabel(deviceType)}
              </Text>
            </Device>
          ))}
        </DeviceWrapper>
      </ScrollView>
    </Wrapper>
  )
}
